/** Legal pages — Privacy Policy, Terms of Use and Intellectual Property Notice.
 * One editorial layout driven by the `page` prop, routed from App.js. */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/brand/Logo";
import LegalFooter from "@/components/brand/LegalFooter";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: February 2026",
    intro:
      "The CCS Approach Consultancy Services (“we”, “our”, “us”) respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how information is collected, used and safeguarded when you visit this website or engage our services.",
    sections: [
      {
        heading: "Information We Collect",
        body: [
          "We may collect information you voluntarily provide, such as your name, email address, phone number and organization when you book a consultation, complete a Needs Assessment, submit a Spotlight nomination, share a workshop reflection or contact us.",
          "We also collect limited, anonymous usage information — such as pages visited — to understand how the website is used and to improve the experience. This information does not personally identify you.",
        ],
      },
      {
        heading: "How We Use Your Information",
        body: [
          "Information you provide is used to respond to enquiries, schedule and deliver consulting and training services, send confirmations and follow-up communication you have requested, and improve our offerings.",
          "We do not sell, rent or trade your personal information to third parties.",
        ],
      },
      {
        heading: "How We Protect Your Information",
        body: [
          "We apply reasonable administrative and technical safeguards to protect your information against unauthorized access, alteration or disclosure. Access to personal information is limited to those who need it to deliver our services.",
        ],
      },
      {
        heading: "Third-Party Services",
        body: [
          "We use trusted third-party providers to support essential functions such as email delivery and messaging. These providers process information only as needed to perform their services on our behalf.",
        ],
      },
      {
        heading: "Your Rights",
        body: [
          "You may request access to, correction of, or deletion of the personal information we hold about you at any time by contacting us through the Contact page.",
        ],
      },
      {
        heading: "Changes to This Policy",
        body: [
          "We may update this Privacy Policy from time to time. The most current version will always be published on this page with the date of the latest revision.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Questions about this Privacy Policy may be submitted through the Contact page or by email to theccsapproach@gmail.com.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    updated: "Last updated: February 2026",
    intro:
      "Welcome to The CCS Approach. By accessing or using this website, you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, please do not use this website.",
    sections: [
      {
        heading: "Use of This Website",
        body: [
          "This website is provided for informational purposes and to facilitate engagement with our consulting and training services. You agree to use it only for lawful purposes and in a manner that does not infringe the rights of, or restrict the use and enjoyment of, this website by any other party.",
        ],
      },
      {
        heading: "Intellectual Property",
        body: [
          "All content on this website — including articles, Spotlight stories, graphics, branding, logos, training materials and the CCS Framework — is the intellectual property of The CCS Approach Consultancy Services unless otherwise stated, and is protected by copyright and other applicable laws. Please see our Intellectual Property Notice for full details.",
        ],
      },
      {
        heading: "Bookings and Services",
        body: [
          "Consultation bookings and service enquiries submitted through this website are requests and do not constitute a confirmed engagement until acknowledged by us. Details of any engagement, including fees and deliverables, are agreed separately in writing.",
        ],
      },
      {
        heading: "User Submissions",
        body: [
          "By submitting content through this website — such as Spotlight nominations, reflections or testimonials — you confirm that the content is accurate to the best of your knowledge and that you have the right to share it. You grant us permission to review, edit for clarity and publish such submissions in connection with our recognition and editorial features.",
        ],
      },
      {
        heading: "Disclaimer",
        body: [
          "The content on this website is provided in good faith for general information. While we strive for accuracy, we make no warranties, express or implied, regarding completeness or suitability for any particular purpose. Nothing on this website constitutes professional advice for your specific circumstances.",
        ],
      },
      {
        heading: "Limitation of Liability",
        body: [
          "To the fullest extent permitted by law, The CCS Approach Consultancy Services shall not be liable for any indirect or consequential loss arising from the use of, or inability to use, this website.",
        ],
      },
      {
        heading: "Changes to These Terms",
        body: [
          "We may revise these Terms of Use at any time. Continued use of the website after changes are published constitutes acceptance of the revised terms.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Questions about these Terms of Use may be submitted through the Contact page or by email to theccsapproach@gmail.com.",
        ],
      },
    ],
  },
  ip: {
    title: "Intellectual Property Notice",
    updated: "Last updated: February 2026",
    intro:
      "Unless otherwise indicated, all content published on this website, including articles, Spotlight stories, graphics, branding, logos, training materials, the CCS Framework, and other original works are protected by copyright and other applicable intellectual property laws.",
    sections: [
      {
        heading: "Use of Content",
        body: [
          "No portion of this website may be copied, reproduced, distributed, modified, or used for commercial purposes without prior written permission from The CCS Approach Consultancy Services.",
        ],
      },
      {
        heading: "Requesting Permission",
        body: [
          "Requests for permission may be submitted through the Contact page.",
        ],
      },
    ],
  },
};

const LegalHeader = () => (
  <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/90 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="legal-header-logo">
        <LogoMark size={40} />
        <div className="leading-tight">
          <div className="font-display font-semibold text-[#2A1810] text-sm">The CCS Approach</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Consultancy Services</div>
        </div>
      </Link>
      <nav className="flex items-center gap-5 text-sm">
        <Link to="/" className="text-[#6B5944] hover:text-[#5D3A1F]" data-testid="legal-nav-home">Home</Link>
        <Link to="/book" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="legal-nav-explore">Explore</Link>
        <Link to="/book#booking" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="legal-nav-contact">Contact</Link>
      </nav>
    </div>
  </header>
);

export default function LegalPage({ page }) {
  const content = CONTENT[page];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${content.title} — The CCS Approach`;
  }, [content.title]);

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid={`legal-page-${page}`}>
      <LegalHeader />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C28A2E]">Legal</div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-light text-[#2A1810]" data-testid="legal-page-title">
          {content.title}
        </h1>
        <p className="mt-3 text-xs text-[#8B7A62]">{content.updated}</p>
        <p className="mt-8 text-base leading-[1.9] text-[#2A1810]/85">{content.intro}</p>
        {content.sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className="font-display text-lg font-semibold text-[#2A1810]">{section.heading}</h2>
            {section.body.map((paragraph, i) => (
              <p key={i} className="mt-3 text-base leading-[1.9] text-[#2A1810]/85">{paragraph}</p>
            ))}
          </section>
        ))}
      </main>
      <footer className="border-t border-[#EBE3D2] py-10 px-6">
        <p className="text-xs italic text-[#8B5A2B] text-center">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
        <LegalFooter className="mt-6" />
      </footer>
    </div>
  );
}
